import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "youtube",
  app: "YouTube",
  title: "How to block YouTube on iPhone without losing the useful half",
  h1: "How to block YouTube",
  description:
    "Screen Time steps, why YouTube is the hardest app to block honestly, and how to keep the tutorials while losing the four hours of recommendations.",
  targets: [
    "how to block youtube on iphone",
    "block youtube app",
    "stop watching youtube",
    "how to block youtube shorts",
  ],
  published: "2026-09-03",
  standfirst:
    "YouTube is the one app where a total block is usually the wrong answer, because half of what is on it is the reason you own a computer.",
  takeaways: [
    "Block the recommendations, not the app. The damage is in the sidebar and the autoplay countdown, not in the video you actually searched for.",
    "Shorts is a separate product wearing YouTube's clothes, and it deserves to be treated the way you would treat TikTok.",
    "Search-and-leave is a workable rule and a browser enforces it better than the app does.",
  ],
  mechanic: [
    "YouTube contains two things that have almost nothing to do with each other. One is the largest instructional archive ever assembled, which people use to fix a boiler, learn a chord or understand a proof. The other is a recommendation engine attached to an autoplay countdown, which is a machine for turning a four-minute intention into three hours.",
    "The join between them is deliberate and it is the mechanic. You arrive for the first thing and the app hands you the second before the first has finished — the next video begins its countdown while the credits are still running, and the sidebar has already been assembled from what people like you watched next.",
    "Shorts made this worse by putting a full vertical infinite feed inside the same app, so YouTube now has a TikTok in it that you can fall into from a thumbnail. Nothing about your reason for opening YouTube protects you from that, which is why the app resists the usual advice: the person telling you to delete it is asking you to give up the archive to escape the casino attached to it.",
  ],
  limitFails:
    "A single app limit treats the archive and the recommendation engine as one thing, so the limit either fires while you are halfway through something you genuinely needed — at which point you dismiss it, and dismissing once is dismissing forever — or you set it generously enough that it never fires at all. That is the specific reason YouTube limits fail more often than any other app's: the app has a legitimate use, so every limit has a legitimate exception, and the exception is the whole game.",
  instead: [
    "Turn off autoplay first. It is one toggle on the player and it removes the countdown that produces most of the extra hours.",
    "Watch in a browser rather than the app, signed out. No history means no personalised sidebar, which is the whole recommendation surface.",
    "Use a Downtime window rather than a daily amount, so the archive stays available during the day and the evening is closed.",
    "Treat Shorts as a different app: if you cannot leave it alone, the honest move is to stop opening it at all rather than to ration it.",
  ],
  verses: [
    {
      verse: {
        text: "Therefore watch carefully how you walk, not as unwise, but as wise, redeeming the time, because the days are evil.",
        ref: "Ephesians 5:15–16",
      },
      note: "Redeeming is a commercial word — buying back, at a cost, something that has been lost or pledged. Time here is treated as a thing already slipping away that has to be actively bought back rather than merely spent well, which is a considerably more urgent posture than time management.",
    },
    {
      verse: {
        text: "and the cares of this age, and the deceitfulness of riches, and the lusts of other things entering in choke the word, and it becomes unfruitful.",
        ref: "Mark 4:19",
      },
      note: "From the parable of the sower, and the striking phrase is the lusts of other things — not bad things, other things. The mechanism described is crowding rather than corruption: nothing in the list is a scandal, and the seed dies anyway because there was no room left. That is a better description of four hours of recommendations than any warning about content.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Autoplay is the whole argument",
      id: "autoplay-is-the-whole-argument",
    },
    {
      type: "p",
      text: "The single highest-value change on this page takes eight seconds: turn off autoplay in the player. It removes the moment where the decision to continue is made for you, and it converts every video into something that ends.",
    },
    {
      type: "p",
      text: "That is the seam other apps have deliberately removed, and YouTube will hand it back if you ask.",
    },
    {
      type: "h2",
      text: "Signed out is a different website",
      id: "signed-out-is-a-different-website",
    },
    {
      type: "p",
      text: "Almost everything that makes YouTube hard to leave is personalised, and personalisation needs to know who you are. A signed-out browser session gives you the search box and the archive with a much duller sidebar attached.",
    },
    {
      type: "note",
      text: "Keep one signed-in browser for subscriptions you genuinely follow, if you have any. Most people discover they do not.",
    },
    {
      type: "h2",
      text: "Search, watch, leave",
      id: "search-watch-leave",
    },
    {
      type: "p",
      text: "The rule that survives contact with real life is not a time budget, it is a shape: arrive with a question, get the answer, close the tab. The tell that you have broken it is that you cannot say what you searched for.",
    },
    {
      type: "p",
      text: "And if the evenings are where this goes, the problem is probably not YouTube specifically — it is that {the last two hours of the day} have no shape, and whatever is on the phone fills them.",
      links: {
        "the last two hours of the day": "/blog/how-to-stop-scrolling-at-night",
      },
    },
  ],
  faq: [
    {
      q: "How do I block YouTube on iPhone?",
      a: "Settings → Screen Time → App Limits → Add Limit → Entertainment, with Block at End of Limit and a passcode. But turn off autoplay in the player first — that one toggle removes more lost time than the limit does.",
    },
    {
      q: "How do I block YouTube Shorts but keep YouTube?",
      a: "There is no setting for it. The workable version is watching in a signed-out browser, where Shorts is much harder to fall into, or treating Shorts as a separate app you have decided not to open.",
    },
    {
      q: "Should I delete YouTube?",
      a: "For most people, no — the instructional half of it is genuinely useful, and advice that ignores that gets ignored. Delete the app and keep the browser, which loses the recommendations and keeps the archive.",
    },
  ],
  relatedApps: ["tiktok", "netflix"],
  related: ["how-to-stop-scrolling-at-night", "screen-time-limits-dont-work"],
  relatedPrayers: ["prayer-for-focus", "prayer-before-you-open-your-phone"],
};
